import { getRootFolders, getFolderContents, FolderItem } from '../groupCreation/groupFoldersApi';
import { getMyGroups, GroupResponse } from '../groupCreation/groupApi';

export interface FolderTreeNode {
    id: string;
    name: string;
    type: 'folder' | 'group';
    children: FolderTreeNode[];
    originalData?: FolderItem | GroupResponse; // Keep reference to original data
    parentIds?: string[]; // IDs of parent folders for easy traversal/checking
}

/**
 * Fetches the full folder and group tree for the current consultant.
 * This can be expensive as it recursively fetches contents for all folders.
 * In a production environment with deep trees, this might need optimization or lazy loading.
 */
export const fetchFolderGroupTree = async (token: string): Promise<FolderTreeNode[]> => {
    try {
        // 1. Fetch root folders (gracefully handle failure — groups can still appear)
        let rootFolders: FolderItem[] = [];
        try {
            const rootFoldersResponse = await getRootFolders(token);
            rootFolders = rootFoldersResponse.data.folders || [];
        } catch (folderErr) {
            console.warn('Could not fetch root folders, continuing with groups only:', folderErr);
        }

        // 2. Fetch all groups to find those that are at the root level (no folderId)
        const allGroupsResponse = await getMyGroups(token, 1, 1000);
        const allGroups = allGroupsResponse.data.groups || [];

        const rootGroups = allGroups.filter(g => !g.folderId);

        // 3. Build tree
        const tree: FolderTreeNode[] = [];

        // Add root groups
        for (const group of rootGroups) {
            tree.push({
                id: group.id,
                name: group.groupName,
                type: 'group',
                children: [],
                originalData: group,
                parentIds: []
            });
        }

        // Add root folders and their contents
        for (const folder of rootFolders) {
            const folderNode = await buildFolderNode(token, folder, [], allGroups);
            tree.push(folderNode);
        }

        return tree;
    } catch (error) {
        console.error('Error fetching folder group tree:', error);
        throw error;
    }
};

const buildFolderNode = async (
    token: string,
    folder: FolderItem,
    parentIds: string[],
    allGroups: GroupResponse[]
): Promise<FolderTreeNode> => {
    const currentPath = [...parentIds, folder.id];

    const node: FolderTreeNode = {
        id: folder.id,
        name: folder.name,
        type: 'folder',
        children: [],
        originalData: folder,
        parentIds: parentIds
    };

    try {
        // Fetch contents of this folder
        const contents = await getFolderContents(token, folder.id);

        // Add child groups
        // The API returns childGroups as any[], assuming they match GroupResponse loosely or we use the allGroups cache
        // If the API returns full group details, use that. Otherwise use the cache if needed.
        // Based on api definition: childGroups: any[]
        // Let's assume the API returns enough info for display

        // Strategy: The getFolderContents API might not return *all* groups if paginated, 
        // but usually folder contents are lists. Alternatively, filter `allGroups` by folderId if available.
        // Let's use the groups returned by getFolderContents if they exist.

        const groupsInFolder = contents.data.childGroups || [];
        // If api doesn't return groups populated, we might need to match with allGroups
        // For now, assume contents.data.childGroups has basic info { id, groupName } at least.

        // Map child groups to nodes
        for (const group of groupsInFolder) {
            node.children.push({
                id: group.id || group._id, // Handle potential messy ID fields
                name: group.groupName || group.name,
                type: 'group',
                children: [],
                originalData: group,
                parentIds: currentPath
            });
        }

        // Recursively process child folders
        const childFolders = contents.data.childFolders || [];
        for (const childFolder of childFolders) {
            const childNode = await buildFolderNode(token, childFolder, currentPath, allGroups);
            node.children.push(childNode);
        }

    } catch (e) {
        console.warn(`Failed to fetch contents for folder ${folder.id}`, e);
        // Return the node without children if failure
    }

    return node;
};

/**
 * Flattens the selected nodes into a list of Group IDs.
 * If a folder is selected, all its descendant groups are included.
 */
export const flattenTreeToGroupIds = (
    tree: FolderTreeNode[],
    selectedIds: string[]
): string[] => {
    const groupIds = new Set<string>();

    const processNode = (node: FolderTreeNode, isParentSelected: boolean) => {
        const isSelected = selectedIds.includes(node.id) || isParentSelected;

        if (node.type === 'group') {
            if (isSelected) {
                groupIds.add(node.id);
            }
        } else {
            // It's a folder
            for (const child of node.children) {
                processNode(child, isSelected);
            }
        }
    };

    for (const node of tree) {
        processNode(node, false);
    }

    return Array.from(groupIds);
};

/**
 * Filters the tree based on a search query.
 * Returns a new tree containing only nodes that match (or have descendants that match).
 */
export const searchTree = (tree: FolderTreeNode[], query: string): FolderTreeNode[] => {
    if (!query) return tree;
    const lowerQuery = query.toLowerCase();

    const filterNode = (node: FolderTreeNode): FolderTreeNode | null => {
        const nameMatches = node.name.toLowerCase().includes(lowerQuery);

        // Recursively filter children
        const filteredChildren: FolderTreeNode[] = [];
        for (const child of node.children) {
            const match = filterNode(child);
            if (match) {
                filteredChildren.push(match);
            }
        }

        // Return node if it matches or has matching children
        if (nameMatches || filteredChildren.length > 0) {
            return {
                ...node,
                children: filteredChildren
            };
        }

        return null;
    };

    const result: FolderTreeNode[] = [];
    for (const node of tree) {
        const match = filterNode(node);
        if (match) {
            result.push(match);
        }
    }

    return result;
};

/**
 * Get all node IDs (folders and groups) from a tree
 */
export const getAllNodeIds = (tree: FolderTreeNode[]): string[] => {
    const ids: string[] = [];
    const traverse = (nodes: FolderTreeNode[]) => {
        for (const node of nodes) {
            ids.push(node.id);
            traverse(node.children);
        }
    };
    traverse(tree);
    return ids;
};
